import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useLang } from '../lib/useLang'
import './Landing.css'
import './PublicPage.css'

const topicIcons = [
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
    <path d='M12 2L2 7l10 5 10-5-10-5z' /><path d='M2 17l10 5 10-5' /><path d='M2 12l10 5 10-5' />
  </svg>,
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
    <rect x='2' y='7' width='20' height='14' rx='2' /><path d='M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2' />
  </svg>,
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
    <circle cx='12' cy='12' r='10' /><path d='M12 8v4l3 3' />
  </svg>,
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
    <circle cx='12' cy='12' r='10' /><path d='M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3' /><line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>,
]

export default function ContactUs() {
  const navigate = useNavigate()
  const { t } = useLang()
  const [step, setStep] = useState('topic')
  const [topicIdx, setTopicIdx] = useState(null)
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)

  const topics = t('contactPage.topics')

  useEffect(() => {
    window.scrollTo(0, 0)
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target) } }),
      { threshold: 0.08 }
    )
    document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const selectTopic = (idx) => {
    setTopicIdx(idx)
    setStep('form')
    setMessage('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSending(true)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, topic: current?.label, message }),
      })
      if (!res.ok) throw new Error()
      setSent(true)
    } catch {
      alert(t('contactPage.sendError') || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const reset = () => {
    setStep('topic')
    setTopicIdx(null)
    setEmail('')
    setMessage('')
    setSent(false)
  }

  const current = Array.isArray(topics) ? topics[topicIdx] : null

  return (
    <div className='public-page'>
      <Helmet>
        <title>Contact Us — MODELLER</title>
        <meta name="description" content="Get in touch with the MODELLER team. Questions about pricing, partnerships, technical support, or anything else." />
      </Helmet>

      {/* Hero */}
      <div className='public-hero'>
        <div className='public-hero-inner'>
          <div data-reveal className='public-tag'>{t('contactPage.tag')}</div>
          <h1 data-reveal className='public-hero-title' style={{ transitionDelay: '0.1s' }}>
            {t('contactPage.title')}
          </h1>
          <p data-reveal className='public-hero-sub' style={{ transitionDelay: '0.2s' }}>
            {t('contactPage.subtitle')}
          </p>
        </div>
      </div>

      {/* Main — shimmer bg like Plans */}
      <div className='public-section-shimmer'>
        <div className='mx-auto max-w-xl px-6 py-20'>

          {/* Step 1 — topic picker */}
          {step === 'topic' && (
            <div className='contact-topics contact-step-enter'>
              {Array.isArray(topics) && topics.map((tp, i) => (
                <button key={i} className='contact-topic-btn' onClick={() => selectTopic(i)}>
                  <span className='contact-topic-icon'>{topicIcons[i]}</span>
                  <span className='contact-topic-label'>{tp.label}</span>
                  <svg className='contact-topic-arrow' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <path d='M5 12h14M12 5l7 7-7 7' />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {/* Step 2 — form */}
          {step === 'form' && !sent && (
            <div className='contact-form-wrap contact-step-enter'>
              <div className='contact-back-row'>
                <button className='contact-back-btn' onClick={reset}>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <path d='M19 12H5M12 19l-7-7 7-7' />
                  </svg>
                  {t('contactPage.back')}
                </button>
                <span className='contact-topic-chip'>
                  {topicIcons[topicIdx]}
                  {current?.label}
                </span>
              </div>

              <p className='contact-form-hint'>{current?.hint}</p>

              <form className='contact-form-inner' onSubmit={handleSubmit}>
                <div className='contact-field'>
                  <label className='contact-label'>{t('contactPage.yourEmail')}</label>
                  <input
                    className='contact-input'
                    type='email'
                    placeholder={t('contactPage.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className='contact-field'>
                  <label className='contact-label'>{t('contactPage.message')}</label>
                  <textarea
                    className='contact-input contact-textarea'
                    placeholder={current?.placeholder}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>
                <button type='submit' className='contact-submit landing-btn-shimmer' disabled={sending}>
                  {sending ? t('contactPage.sending') || 'Sending...' : t('contactPage.sendMessage')}
                </button>
              </form>
            </div>
          )}

          {/* Sent */}
          {sent && (
            <div className='contact-sent contact-step-enter'>
              <div className='contact-sent-icon'>
                <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                  <path d='M20 6L9 17l-5-5' />
                </svg>
              </div>
              <h3 className='contact-sent-title'>{t('contactPage.messageSent')}</h3>
              <p className='contact-sent-sub'>{current?.hint}</p>
              <button onClick={reset} className='contact-sent-back'>{t('contactPage.askElse')}</button>
            </div>
          )}

        </div>
      </div>

      {/* Contact details strip */}
      <div className='public-section-dark'>
        <div className='mx-auto max-w-3xl px-6 py-16'>
          <div data-reveal className='contact-strip' style={{ justifyContent: 'center' }}>
            <div className='contact-strip-item'>
              <span className='contact-strip-icon'>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'><path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' /><polyline points='22,6 12,13 2,6' /></svg>
              </span>
              <div>
                <div className='contact-strip-label'>{t('contactPage.emailLabel')}</div>
                <a href='mailto:contact@modeller.tech' className='contact-strip-value contact-strip-link'>contact@modeller.tech</a>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
